import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import { getRingProBuyPageFaqData } from 'content/products/ring/faqData';
import {
  generateProductSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  toResolvedFaqItems,
} from 'lib/seo/schema';
import { extractTextFromJSX } from 'utils/extractTextFromJSX';
import { getAssetUrl } from 'lib/config/assetUrls';
import { getRingProBasePrice } from 'content/products/ring-pro/tierPricing';
import { RING_PRO_SEO_DESCRIPTION } from 'lib/seo/meta/seoImage';

type JsonLdObject = Record<string, unknown>;

type TFunction = (
  key: string,
  values?: Record<string, string | number>,
) => string;

interface RingProPricingSchemaParams {
  locale: string;
  country: string;
  /** Path slug for canonical URLs; pass from RegionLocaleContext so ROW locales are not `/row/...`. */
  regionSlug?: string;
  store: ShopifyStore;
  currency: Currency;
  price?: number;
  t?: TFunction;
}

export const getRingProPricingSchemas = ({
  locale,
  country,
  regionSlug,
  store,
  currency,
  price,
  t,
}: RingProPricingSchemaParams): JsonLdObject[] => {
  const localeUpper = (locale || 'us').toUpperCase();
  const localeLower = localeUpper.toLowerCase();
  const countryLower = (country || localeLower).toLowerCase();
  const countryUpper = countryLower.toUpperCase();
  const urlSlug = (regionSlug || localeLower).toLowerCase();

  const resolvedPrice = price ?? getRingProBasePrice(store, currency) ?? 479;
  const currencyString = currency.toUpperCase();

  const defaultT: TFunction = (key) => key;
  const faqSource = getRingProBuyPageFaqData(t || defaultT);

  const productSchema = generateProductSchema({
    name: 'Ultrahuman Ring PRO',
    description: RING_PRO_SEO_DESCRIPTION,
    imageUrl: getAssetUrl('/web_v2/meta-assets/ring-pro-og-lg.png'),
    brand: 'Ultrahuman',
    category: 'Wearable Technology',
    // TODO: replace sku/mpn placeholders with real values from Shopify.
    // No aggregateRating until a real reviews source backs it — fabricated
    // ratings violate Google's structured-data policy.
    sku: 'UH-RING-PRO-001',
    mpn: 'UHRINGPRO',
    price: resolvedPrice,
    currency: currencyString,
    country: countryUpper,
    availability: 'InStock',
    url: `https://www.ultrahuman.com/${urlSlug}/ring-pro/buy`,
  });

  const faqItems = toResolvedFaqItems(
    faqSource.map((faq) => ({
      question: extractTextFromJSX(faq.title) || '',
      answer: extractTextFromJSX(faq.description) || '',
    })),
  );

  const faqSchema = generateFAQSchema(faqItems);

  const breadcrumbSchema = generateBreadcrumbSchema([
    {
      name: 'Home',
      url: 'https://www.ultrahuman.com',
    },
    {
      name: 'Ultrahuman Ring',
      url: 'https://www.ultrahuman.com/ring-pro',
    },
    {
      name: 'Buy Ring PRO',
      url: `https://www.ultrahuman.com/${urlSlug}/ring-pro/buy`,
    },
  ]);

  return [productSchema, faqSchema, breadcrumbSchema];
};
